import ReactECharts from 'echarts-for-react';
import { useEffect, useRef, memo } from 'react';

const DataChart = memo(({ title, xData, yData, yAxisName }) => {
  const chartRef = useRef(null);
  const isFirstRender = useRef(true);
  const ANIMATION_DURATION = 2000; // 設置動畫總時長

  const getChartOption = (showAnimation = true) => {
    // 計算數據的最大值和最小值
    const maxValue = Math.max(...(yData || []));
    const minValue = Math.min(...(yData || []));
    
    // 計算 Y 軸的最大值和最小值
    const yAxisMax = maxValue * 1.05;
    const yAxisMin = minValue * 0.95;
    
    // 計算合適的間隔
    const interval = (yAxisMax - yAxisMin) / 5;

    return {
      title: {
        text: title,
        textStyle: {
          color: '#fff',
          fontSize: 18,
          fontWeight: 'bold'
        },
        left: 'center',
        top: 10,
        padding: [5, 0, 20, 0]
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          axis: 'x',
          label: {
            backgroundColor: '#1890ff'
          }
        },
        formatter: function(params) {
          const dataIndex = params[0].dataIndex;
          const fullDateTime = xData[dataIndex];
          const value = params[0].value;
          
          const [datePart, timePart] = fullDateTime.split(' ');
          const [year, month, day] = datePart.split('-');
          const [hour] = timePart.split(':');
          
          return `
            <div style="padding: 3px;">
              <div style="margin-bottom: 5px; font-size: 14px;">
                ${year}年${month}月${day}日${hour}時
              </div>
              <div style="font-size: 13px;">
                ${yAxisName}：${value}
              </div>
            </div>
          `;
        },
        backgroundColor: 'rgba(0, 43, 86, 0.9)',
        borderColor: '#1890ff',
        textStyle: {
          color: '#fff',
          fontSize: 12
        },
        padding: [8, 12],
        extraCssText: 'box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);'
      },
      grid: {
        left: '10%',
        right: '5%',
        bottom: '12%',
        top: '15%',
        width: '87.5%',
        height: '70%',
        containLabel: false
      },
      xAxis: {
        type: 'category',
        data: xData?.map((datetime, index) => {
          const [datePart, timePart] = datetime.split(' ');
          const [year, month, day] = datePart.split('-');
          const [hour] = timePart.split(':');
          
          // 只在每天的第一個小時（00時）顯示日期
          return hour === '00' ? `${day}` : '';
        }) || [],
        axisLabel: {
          color: '#fff',
          rotate: 45,
          margin: 15,
          fontSize: 12,
          interval: 0,  // 顯示所有標籤
          align: 'center',
          formatter: (value) => value || ''  // 空字符串不顯示
        },
        name: '日期',
        nameLocation: 'middle',
        nameGap: 35,
        nameTextStyle: {
          fontWeight: 'bold',
          color: '#fff',
          fontSize: 14,
          padding: [15, 0, 0, 0]
        },
        boundaryGap: true,
        axisTick: {
          show: true,
          alignWithLabel: true,
          interval: (index) => {
            // 只在每天的第一個小時顯示刻度
            if (!xData) return false;
            const hour = xData[index].split(' ')[1].split(':')[0];
            return hour === '00';
          }
        },
      },
      yAxis: {
        type: 'value',
        name: yAxisName,
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          fontWeight: 'bold',
          color: '#fff',
          fontSize: 14,
          padding: [0, 0, 5, 0]
        },
        min: yAxisMin,
        max: yAxisMax,
        interval: interval,
        axisLabel: {
          color: '#fff',
          margin: 12,
          fontSize: 12,
          width: 60,
          align: 'right',
          formatter: (value) => value.toFixed(1)
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.1)',
            type: 'dashed'
          }
        },
        axisTick: {
          length: 5,
          lineStyle: {
            color: '#fff'
          }
        },
        axisLine: {
          lineStyle: {
            color: '#fff',
            width: 1
          }
        }
      },
      series: [{
        data: showAnimation ? [] : (yData || []),
        type: 'line',
        smooth: true,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
          color: '#1890ff'
        },
        emphasis: {
          focus: 'series',
          scale: false,
        },
        itemStyle: {
          color: '#1890ff',
          borderWidth: 2,
          borderColor: '#fff'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0,
              color: 'rgba(24, 144, 255, 0.5)'
            }, {
              offset: 1,
              color: 'rgba(24, 144, 255, 0.1)'
            }]
          }
        }
      }]
    };
  };

  useEffect(() => {
    let chart = null;
    
    if (chartRef.current && xData && yData) {
      chart = chartRef.current.getEchartsInstance();
      
      const renderAnimation = () => {
        // 確保圖表實例仍然存在
        if (!chart || chart.isDisposed()) {
          return;
        }

        const totalPoints = yData.length;
        const frameRate = 60;
        const totalFrames = Math.ceil(ANIMATION_DURATION / (1000 / frameRate));
        const pointsPerFrame = Math.ceil(totalPoints / totalFrames);
        let currentIndex = 0;

        const addDataPoint = () => {
          // 再次檢查圖表實例
          if (!chart || chart.isDisposed()) {
            return;
          }

          if (currentIndex < totalPoints) {
            const nextIndex = Math.min(currentIndex + pointsPerFrame, totalPoints);
            const currentData = yData.slice(0, nextIndex);
            chart.setOption({
              series: [{
                data: currentData
              }]
            });
            currentIndex = nextIndex;

            if (currentIndex < totalPoints) {
              requestAnimationFrame(addDataPoint);
            }
          }
        };

        requestAnimationFrame(addDataPoint);
      };
      
      if (isFirstRender.current) {
        const option = getChartOption(true);
        chart.setOption(option);
        renderAnimation();
        isFirstRender.current = false;
      } else {
        chart.clear();
        const option = getChartOption(true);
        chart.setOption(option);
        renderAnimation();
      }
      
      chart.resize();
    }

    // 清理函數
    return () => {
      if (chart && !chart.isDisposed()) {
        chart.dispose();
      }
    };
  }, [xData, yData, title]);

  return (
    <div style={{ height: '450px', width: '100%', padding: '0px', margin: '0px' }}>
      <ReactECharts
        ref={chartRef}
        option={getChartOption(false)}
        style={{ height: '100%', width: '100%', padding: '0px', margin: '0px' }}
        opts={{ 
          renderer: 'svg',
          animation: true 
        }}
        notMerge={false}
        lazyUpdate={true}
      />
    </div>
  );
});

DataChart.displayName = 'DataChart';

export default DataChart; 